import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import ShippingInfo from '../components/ShippingInfo';
import Footer from '../components/Footer';
import { useToast } from '../context/ToastContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { API_CONFIG } from '../config/apiConfig';
import { getOffers, getWalletBalance } from '../api/authApi';
import ProductCard from '../components/ProductCard';

const CartPage = () => {
  const navigate = useNavigate();
  const cart = useCart();
  const { user, token } = useAuth();
  const toast = useToast();
  
  // State
  const [couponCode, setCouponCode] = useState('');
  const [note, setNote] = useState('');
  const [offers, setOffers] = useState([]);
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [walletBalance, setWalletBalance] = useState(0);
  const [useWallet, setUseWallet] = useState(false);
  const [loading, setLoading] = useState(false);

  // Load offers and wallet balance on mount
  useEffect(() => {
    loadOffers();
    if (user && token) {
      loadWalletBalance();
    }
  }, [user, token]);


  // Sync wallet checkbox with cart context
  useEffect(() => {
    if (cart.walletDiscount > 0) {
      setUseWallet(true);
    } else {
      setUseWallet(false);
    }
  }, [cart.walletDiscount]);

  const loadOffers = async () => {
    try {
      const result = await getOffers();
      if (result.success) {
        setOffers(result.data || []);
      }
    } catch (error) {
      console.error('Failed to load offers:', error);
    }
  };

  const loadWalletBalance = async () => {
    try {
      const result = await getWalletBalance(user.id, token);
      if (result.success) {
        setWalletBalance(result.balance);
      }
    } catch (error) {
      console.error('Failed to load wallet balance:', error);
    }
  };

  const updateQuantity = (id, newQuantity, variant = null) => {
    if (newQuantity < 1) return;
    cart.updateQuantity(id, newQuantity, variant);
  };

  const removeItem = (id, variant = null) => {
    cart.removeItem(id, variant);
  };

  const getItemImage = (item) => {
    if (item.cover && typeof item.cover === 'string' && item.cover.trim()) {
      return item.cover.startsWith('http') ? item.cover : `${API_CONFIG.IMAGE_URL}${item.cover}`;
    }
    return '/assets/img/product/default.png';
  };

  const parsePrice = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
    const str = String(value).trim();
    if (str === '') return 0;
    const cleaned = str.replace(/[^0-9.-]+/g, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : 0;
  };

  const getItemPrice = (item) => {
    // For variation products with discount field, use discount as the final price
    if (item.variant !== null && item.variant !== undefined && item.variations && item.variations[0]?.items?.[item.variant]) {
      const variantData = item.variations[0].items[item.variant];
      const variantDiscount = parseFloat(variantData.discount) || 0;
      
      if (variantDiscount > 0) {
        // Use discount field as the final price
        return variantDiscount;
      }
      // No discount field, use variant price
      return parsePrice(variantData.price);
    }
    
    // For non-variation products, use sell_price or original_price
    const discountValue = parseFloat(item.discount) || 0;
    if (discountValue === 0) {
      return parsePrice(item.original_price);
    } else {
      return parsePrice(item.sell_price);
    }
  };

  const getItemTotal = (item) => {
    return (getItemPrice(item) * item.quantiy).toFixed(2);
  };

  // Apply coupon code
  const handleApplyCoupon = async (e) => {
    e?.preventDefault();
    
    if (!couponCode.trim()) {
      showMessage('error', 'Please enter a coupon code');
      return;
    }

    // Check if wallet is already applied
    if (cart.walletDiscount > 0) {
      showMessage('error', 'Sorry you have already added a wallet discount to cart');
      return;
    }

    const entered = offers.filter(x => x.name === couponCode || x.code === couponCode);
    
    if (entered && entered.length > 0) {
      const offer = entered[0];
      const min = parseFloat(offer.min);
      
      if (cart.totalPrice >= min) {
        cart.applyCoupon(offer);
        showMessage('success', 'Coupon applied successfully');
        setCouponCode('');
      } else {
        showMessage('error', `Minimum cart value must be $${min} or equal`);
      }
    } else {
      showMessage('error', 'Offer not found');
    }
  };

  // Select offer from list
  const handleSelectOffer = (offer) => {
    // Check if wallet is already applied
    if (cart.walletDiscount > 0) {
      showMessage('error', 'Sorry you have already added a wallet discount to cart');
      return;
    }

    const min = parseFloat(offer.min);
    
    if (cart.totalPrice >= min) {
      cart.applyCoupon(offer);
      showMessage('success', `Coupon "${offer.name}" applied successfully`);
      setShowOffersModal(false);
    } else {
      showMessage('error', `Minimum cart value must be $${min} or equal`);
    }
  };

  // Remove coupon
  const handleRemoveCoupon = () => {
    cart.removeCoupon();
    showMessage('success', 'Coupon removed');
  };

  // Handle wallet checkbox
  const handleWalletChange = (e) => {
    const checked = e.target.checked;
    
    if (checked) {
      // Check if coupon is already applied
      if (cart.coupon && cart.coupon.id) {
        showMessage('error', 'Sorry you have already added a offers discount to cart');
        setUseWallet(false);
        return;
      }
      
      cart.applyWalletDiscount(walletBalance);
      setUseWallet(true);
      showMessage('success', 'Wallet discount applied');
    } else {
      cart.applyWalletDiscount(0);
      setUseWallet(false);
      showMessage('success', 'Wallet discount removed');
    }
  };

  // Show message helper
  const showMessage = (type, text) => {
    if (type === 'success') {
      toast.showSuccess(text);
    } else if (type === 'error') {
      toast.showError(text);
    } else if (type === 'info') {
      toast.showInfo(text);
    } else {
      toast.showToast(text, type);
    }
  };

  // Check minimum order before proceeding
  const handleProceedToCheckout = () => {
    if (cart.totalPrice < cart.minOrderPrice) {
      showMessage('error', `Minimum order amount must be $${cart.minOrderPrice} or more`);
      return;
    }
    navigate('/checkout');
  };

  // Recommended products
  const [fetchedRelated, setFetchedRelated] = useState([]);

  const recommendedProducts = React.useMemo(() => {
    if (cart.cart.length === 0) return [];

    const highestQtyItem = cart.cart.reduce((max, item) => 
      item.quantiy > max.quantiy ? item : max
    );

    if (fetchedRelated.length > 0) {
      return fetchedRelated;
    }

    if (highestQtyItem.related && Array.isArray(highestQtyItem.related)) {
      const unique = highestQtyItem.related.filter((product, index, self) => 
        index === self.findIndex(p => p.id === product.id)
      );
      return unique.slice(0, 6);
    }

    return [];
  }, [cart.cart, fetchedRelated]);

  useEffect(() => {
    if (cart.cart.length === 0) {
      setFetchedRelated([]);
      return;
    }

    const highestQtyItem = cart.cart.reduce((max, item) => 
      item.quantiy > max.quantiy ? item : max
    );

    const fetchRelated = async () => {
      try {
        const res = await fetch(`${API_CONFIG.BASE_URL}/v1/products/getById`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: highestQtyItem.id })
        });
        if (res.ok) {
          const json = await res.json();
          if (json.data && json.related && Array.isArray(json.related)) {
            // Process related products to ensure they have the required fields for ProductCard
            const processedRelated = json.related.map(related => ({
              id: related.id,
              name: related.name || 'Unknown Product',
              original_price: parseFloat(related.original_price) || parseFloat(related.price) || 0,
              sell_price: parseFloat(related.sell_price) || parseFloat(related.price) || parseFloat(related.original_price) || 0,
              price: parseFloat(related.sell_price) || parseFloat(related.price) || parseFloat(related.original_price) || 0,
              discount: parseFloat(related.discount) || 0,
              cover: related.cover || related.image || '',
              images: related.images || [],
              descriptions: related.descriptions || related.description || '',
              rating: related.rating || 0,
              reviews: related.total_rating || 0,
              in_stock: Number(related.in_stoke ?? related.in_stock ?? 1),
              // CRITICAL: Keep size as string for ProductCard variant check
              size: related.size ? String(related.size) : '0',
              variations: related.variations || [],
              cate_id: related.cate_id,
              sub_cate_id: related.sub_cate_id,
              store_id: related.store_id,
              store_name: related.store_name || 'Default Store'
            }));

            setFetchedRelated(processedRelated.slice(0, 6));
          }
        }
      } catch (error) {
        console.error('Failed to fetch related products:', error);
      }
    };

    fetchRelated();
  }, [cart.cart]);

  return (
    <>
      <Header />

      <main className="main__content_wrapper">
        <Breadcrumb title="Shopping Cart" items={[{ label: 'Shopping Cart' }]} />

        <section className="cart__section section--padding">
          <div className="container-fluid">
            <div className="cart__section--inner">
              <h2 className="cart__title mb-40">Shopping Cart</h2>
              <form action="#">
                <div className="row">
                  <div className="col-lg-8">
                    <div className="cart__table">
                      <table className="cart__table--inner">
                        <thead className="cart__table--header">
                          <tr className="cart__table--header__items">
                            <th className="cart__table--header__list">Product</th>
                            <th className="cart__table--header__list">Price</th>
                            <th className="cart__table--header__list">Quantity</th>
                            <th className="cart__table--header__list">Total</th>
                          </tr>
                        </thead>
                        <tbody className="cart__table--body">
                          {cart.cart.length === 0 ? (
                            <tr>
                              <td colSpan="4" className="text-center py-4">
                                Your cart is empty
                              </td>
                            </tr>
                          ) : (
                            cart.cart.map((item) => (
                              <tr key={item.id} className="cart__table--body__items">
                                <td className="cart__table--body__list">
                                  <div className="cart__product d-flex align-items-center">
                                    <button
                                      className="cart__remove--btn"
                                      type="button"
                                      onClick={() => removeItem(item.id, item.variant)}
                                    >
                                      <svg fill="currentColor" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16px" height="16px">
                                        <path d="M 4.7070312 3.2929688 L 3.2929688 4.7070312 L 10.585938 12 L 3.2929688 19.292969 L 4.7070312 20.707031 L 12 13.414062 L 19.292969 20.707031 L 20.707031 19.292969 L 13.414062 12 L 20.707031 4.7070312 L 19.292969 3.2929688 L 12 10.585938 L 4.7070312 3.2929688 z" />
                                      </svg>
                                    </button>
                                    <div className="cart__thumbnail">
                                      <img className="border-radius-5" src={getItemImage(item)} alt={item.name} />
                                    </div>
                                    <div className="cart__content">
                                      <h4 className="cart__content--title">
                                        <Link to={`/product/${item.id}`}>{item.name}{item.variant !== null && item.variant !== undefined && item.variations && item.variations[0]?.items?.[item.variant] ? ` (${item.variations[0].items[item.variant].name})` : ''}</Link>
                                      </h4>
                                      {item.store_name && item.store_name !== 'Default Store' && (
                                        <span className="cart__content--variant">STORE: {item.store_name}</span>
                                      )}
                                      {parseFloat(item.discount) > 0 && (
                                        <span className="cart__content--variant">DISCOUNT: {item.discount}% OFF</span>
                                      )}
                                    </div>
                                  </div>
                                </td>
                                <td className="cart__table--body__list">
                                  <div className="cart__price__wrapper">
                                    {parseFloat(item.discount) > 0 ? (
                                      <>
                                        <span className="cart__price">${getItemPrice(item).toFixed(2)}</span>
                                        <span className="cart__price--old" style={{ textDecoration: 'line-through', color: '#999', fontSize: '14px', marginLeft: '8px' }}>
                                          ${parseFloat(item.original_price).toFixed(2)}
                                        </span>
                                      </>
                                    ) : (
                                      <span className="cart__price">${getItemPrice(item).toFixed(2)}</span>
                                    )}
                                  </div>
                                </td>
                                <td className="cart__table--body__list">
                                  <div className="quantity__box">
                                    <button 
                                      type="button" 
                                      className="quantity__value quickview__value--quantity decrease" 
                                      onClick={() => updateQuantity(item.id, item.quantiy - 1, item.variant)}
                                    >-</button>
                                    <label>
                                      <input 
                                        type="number" 
                                        className="quantity__number quickview__value--number" 
                                        value={item.quantiy} 
                                        onChange={(e) => updateQuantity(item.id, parseInt(e.target.value) || 1, item.variant)} 
                                      />
                                    </label>
                                    <button 
                                      type="button" 
                                      className="quantity__value quickview__value--quantity increase" 
                                      onClick={() => updateQuantity(item.id, item.quantiy + 1, item.variant)}
                                    >+</button>
                                  </div>
                                </td>
                                <td className="cart__table--body__list">
                                  <span className="cart__price end">${getItemTotal(item)}</span>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                      <div className="continue__shopping d-flex justify-content-between">
                        <Link className="continue__shopping--link" to="/shop">Continue shopping</Link>
                        <button className="continue__shopping--clear" type="button" onClick={() => cart.clearCart()}>Clear Cart</button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-lg-4">
                    <div className="cart__summary border-radius-10">
                      {/* Coupon Section */}
                      <div className="coupon__code mb-30">
                        <h3 className="coupon__code--title">Coupon</h3>
                        <p className="coupon__code--desc">Enter your coupon code if you have one.</p>
                        
                        {cart.coupon ? (
                          <div className="applied-coupon mb-15">
                            <div className="d-flex justify-content-between align-items-center p-3 bg-light border-radius-5">
                              <div>
                                <strong>{cart.coupon.name || cart.coupon.code}</strong>
                                <br />
                                <small>Discount: ${cart.discount.toFixed(2)}</small>
                              </div>
                              <button 
                                type="button" 
                                className="btn btn-sm btn-danger"
                                onClick={handleRemoveCoupon}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div className="coupon__code--field d-flex mb-15">
                              <label className="flex-grow-1">
                                <input 
                                  className="coupon__code--field__input border-radius-5" 
                                  placeholder="Coupon code" 
                                  type="text" 
                                  value={couponCode} 
                                  onChange={(e) => setCouponCode(e.target.value)}
                                  onKeyPress={(e) => e.key === 'Enter' && handleApplyCoupon(e)}
                                />
                              </label>
                              <button 
                                className="coupon__code--field__btn btn" 
                                type="button"
                                onClick={handleApplyCoupon}
                              >
                                Apply
                              </button>
                            </div>
                            {offers.length > 0 && (
                              <button 
                                type="button" 
                                className="btn btn-sm btn-outline-primary w-100"
                                onClick={() => setShowOffersModal(true)}
                              >
                                View Available Offers ({offers.length})
                              </button>
                            )}
                          </>
                        )}
                      </div>

                      {/* Wallet Section */}
                      {user && walletBalance > 0 && (
                        <div className="wallet__section mb-30">
                          <h3 className="wallet__title">Wallet</h3>
                          <div className="wallet__balance mb-15">
                            <p>Available Balance: <strong>${walletBalance.toFixed(2)}</strong></p>
                          </div>
                          <div className="form-check">
                            <input 
                              className="form-check-input" 
                              type="checkbox" 
                              id="useWallet"
                              checked={useWallet}
                              onChange={handleWalletChange}
                              disabled={cart.coupon && cart.coupon.id}
                            />
                            <label className="form-check-label" htmlFor="useWallet">
                              Use wallet balance
                            </label>
                          </div>
                        </div>
                      )}

                      {/* Note Section */}
                      <div className="cart__note mb-20">
                        <h3 className="cart__note--title">Note</h3>
                        <p className="cart__note--desc">Add special instructions for your seller...</p>
                        <textarea 
                          className="cart__note--textarea border-radius-5" 
                          value={note} 
                          onChange={(e) => setNote(e.target.value)}
                        ></textarea>
                      </div>

                      {/* Summary Section */}
                      <div className="cart__summary--total mb-20">
                        <table className="cart__summary--total__table">
                          <tbody>
                            <tr className="cart__summary--total__list">
                              <td className="cart__summary--total__title text-left">SUBTOTAL</td>
                              <td className="cart__summary--amount text-right">${cart.totalPrice.toFixed(2)}</td>
                            </tr>
                            {cart.discount > 0 && (
                              <tr className="cart__summary--total__list">
                                <td className="cart__summary--total__title text-left">COUPON DISCOUNT</td>
                                <td className="cart__summary--amount text-right text-success">-${cart.discount.toFixed(2)}</td>
                              </tr>
                            )}
                            {cart.walletDiscount > 0 && (
                              <tr className="cart__summary--total__list">
                                <td className="cart__summary--total__title text-left">WALLET DISCOUNT</td>
                                <td className="cart__summary--amount text-right text-success">-${cart.walletDiscount.toFixed(2)}</td>
                              </tr>
                            )}
                            {cart.deliveryPrice > 0 && (
                              <tr className="cart__summary--total__list">
                                <td className="cart__summary--total__title text-left">DELIVERY</td>
                                <td className="cart__summary--amount text-right">${cart.deliveryPrice.toFixed(2)}</td>
                              </tr>
                            )}
                            <tr className="cart__summary--total__list">
                              <td className="cart__summary--total__title text-left">TAX ({cart.orderTax}%)</td>
                              <td className="cart__summary--amount text-right">${(cart.totalPrice * cart.orderTax / 100).toFixed(2)}</td>
                            </tr>
                            <tr className="cart__summary--total__list">
                              <td className="cart__summary--total__title text-left"><strong>GRAND TOTAL</strong></td>
                              <td className="cart__summary--amount text-right"><strong>${cart.grandTotal.toFixed(2)}</strong></td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* Minimum Order Warning */}
                      {cart.totalPrice < cart.minOrderPrice && (
                        <div className="alert alert-warning mb-20">
                          <small>Minimum order amount: ${cart.minOrderPrice.toFixed(2)}</small>
                        </div>
                      )}

                      {/* Action Buttons */}
                      <div className="cart__summary--footer">
                        <p className="cart__summary--footer__desc">Shipping & taxes calculated at checkout</p>
                        <ul className="d-flex justify-content-between">
                          <li>
                            <button className="cart__summary--footer__btn btn cart" type="button">
                              Update Cart
                            </button>
                          </li>
                          <li>
                            <button 
                              className="cart__summary--footer__btn btn checkout" 
                              type="button"
                              onClick={handleProceedToCheckout}
                              disabled={cart.cart.length === 0}
                            >
                              Check Out
                            </button>
                          </li>
                        </ul>
                      </div>
                      </div>
                    </div>

                  </div>
                </form>
              </div>
            </div>
          </section>
        
          {/* Recommended Products */}
          {recommendedProducts.length > 0 && (
            <section className="product__section product__section--style3 section--padding pt-0">
              <div className="container-fluid">
                <div className="section__heading3 mb-40">
                  <h2 className="section__heading3--maintitle">Recommended Products</h2>
                </div>
                <div className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-4">
                  {recommendedProducts.map((product) => (
                    <div key={product.id} className="col">
                      <ProductCard product={product} />
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        
          <ShippingInfo />
        </main>
      
        <Footer />
      
        {/* Offers Modal */}
        {showOffersModal && (
          <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Available Offers</h5>
                  <button type="button" className="btn-close" onClick={() => setShowOffersModal(false)}></button>
                </div>
                <div className="modal-body">
                  {offers.length === 0 ? (
                    <p>No offers available at the moment.</p>
                  ) : (
                    <div className="offers-list">
                      {offers.map((offer) => (
                        <div key={offer.id} className="offer-item mb-3 p-3 border border-radius-5">
                          <div className="d-flex justify-content-between align-items-start">
                            <div className="flex-grow-1">
                              <h6 className="mb-1">{offer.name || offer.code}</h6>
                              <p className="mb-1 text-muted small">
                                {offer.type === 'per' ? `${offer.off}% OFF` : `$${offer.off} OFF`}
                              </p>
                              <p className="mb-0 text-muted small">
                                Min. order: ${offer.min}
                              </p>
                              {offer.description && (
                                <p className="mb-0 text-muted small mt-1">{offer.description}</p>
                              )}
                            </div>
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => handleSelectOffer(offer)}
                              disabled={cart.totalPrice < parseFloat(offer.min)}
                            >
                              Apply
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </>
    );
};

export default CartPage;
